import { apiService } from '@/lib/api-functions/api-service';

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  id?: string;
  imagekey?: string;
  path?: string;
  error?: string;
}

export class ImageService {
  /**
   * Upload an image file via API
   * @param file - The image file to upload
   * @param gameId - The game ID to organize the image
   * @returns Promise with upload result
   */
  async uploadImage(file: File, gameId: string): Promise<ImageUploadResult> {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return {
          success: false,
          error: 'File must be an image (PNG, JPG, SVG, etc.)'
        };
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        return {
          success: false,
          error: 'File size must be less than 10MB'
        };
      }

      // Validate gameId
      if (!gameId) {
        return {
          success: false,
          error: 'Game ID is required for image upload'
        };
      }

      // Upload via API service
      const result = await apiService.uploadImage(file, gameId);
      
      if (result.success && result.data) {
        return {
          success: true,
          url: result.data.url,
          id: result.data.id,
          imagekey: result.data.imagekey,
          path: result.data.path
        };
      } else {
        return {
          success: false,
          error: result.error || 'Upload failed'
        };
      }

    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error'
      };
    }
  }

  /**
   * Upload image from data URL (base64)
   * @param dataUrl - Base64 data URL of the image
   * @param gameId - The game ID to organize the image
   * @param filename - Optional filename (defaults to generated name)
   * @returns Promise with upload result
   */
  async uploadImageFromDataUrl(dataUrl: string, gameId: string, filename?: string): Promise<ImageUploadResult> {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Create file from blob
      const file = new File([blob], filename || 'image.png', { type: blob.type });
      
      // Use regular upload method
      return this.uploadImage(file, gameId);
    } catch (error) {
      console.error('Data URL upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process image data'
      };
    }
  }

  /**
   * Delete an image via API
   * @param url - Public URL of the image to delete
   * @returns Promise with deletion result
   */
  async deleteImage(url: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Delete via API service
      const result = await apiService.deleteImage(url);
      
      if (result.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: result.error || 'Delete failed'
        };
      }
    } catch (error) {
      console.error('Image delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown delete error'
      };
    }
  }
}

// Export singleton instance
export const imageService = new ImageService();
